import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import adminAPI from "./apiClient";
import type { ConversationSummary, PaginationInfo, ConversationFilters } from "./types";
import { getTotalCostFromApi, formatCost } from "./modelPricing";
import { channelMeta } from "./channelMeta";
import { formatDate, getTimezoneAbbreviation } from "../../utils/formatDate";

export default function ConversationsView(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [filters, setFilters] = useState<ConversationFilters>({
    username: "",
    environment: "",
    channel: "",
    dateFrom: "",
    dateTo: "",
    search: "",
    hasErrors: undefined,
    hasPositiveFeedback: undefined,
    hasNegativeFeedback: undefined,
    hitLimits: undefined,
    minTimeMs: "",
    maxTimeMs: "",
    page: 1,
    page_size: 50
  });

  // Check for conversation ID in query parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const conversationIdParam = urlParams.get('conversationId');

    if (conversationIdParam) {
      // Navigate directly to the conversation detail page
      navigate(`/admin/conversation/${conversationIdParam}`);
    }
  }, [location.search, navigate]);

  // Fetch conversations from API
  useEffect(() => {
    fetchConversations();
  }, [filters]);  // Refetch when filters change

  const fetchConversations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminAPI.getConversations(filters);
      setConversations(response.conversations);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof ConversationFilters, value: string | boolean | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1  // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      username: "",
      environment: "",
      channel: "",
      dateFrom: "",
      dateTo: "",
      search: "",
      hasErrors: undefined,
      hasPositiveFeedback: undefined,
      hasNegativeFeedback: undefined,
      hitLimits: undefined,
      minTimeMs: "",
      maxTimeMs: "",
      page: 1,
      page_size: 50
    });
  };

  const formatDuration = (milliseconds: number): string => {
    if (!milliseconds || milliseconds === 0) return "—";
    if (milliseconds < 1000) return `${milliseconds}ms`;
    if (milliseconds < 60000) return `${(milliseconds / 1000).toFixed(1)}s`;
    return `${(milliseconds / 60000).toFixed(1)}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>

        {/* Quick Filters */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleFilterChange("hasErrors", true)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filters.hasErrors === true
                  ? "bg-red-600 text-white"
                  : "bg-red-100 text-red-700 hover:bg-red-200"
              }`}
            >
              ⚠️ With Errors
            </button>
            <button
              onClick={() => handleFilterChange("hasPositiveFeedback", true)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filters.hasPositiveFeedback === true
                  ? "bg-green-600 text-white"
                  : "bg-green-100 text-green-700 hover:bg-green-200"
              }`}
            >
              👍 Positive Feedback
            </button>
            <button
              onClick={() => handleFilterChange("hasNegativeFeedback", true)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filters.hasNegativeFeedback === true
                  ? "bg-red-600 text-white"
                  : "bg-red-100 text-red-700 hover:bg-red-200"
              }`}
            >
              👎 Negative Feedback
            </button>
            <button
              onClick={() => handleFilterChange("hitLimits", true)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filters.hitLimits === true
                  ? "bg-orange-600 text-white"
                  : "bg-orange-100 text-orange-700 hover:bg-orange-200"
              }`}
            >
              🚫 Rate Limited
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={filters.search || ''}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Conversation ID or Username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              value={filters.username || ''}
              onChange={(e) => handleFilterChange("username", e.target.value)}
              placeholder="Filter by username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Environment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Environment
            </label>
            <select
              value={filters.environment || ''}
              onChange={(e) => handleFilterChange("environment", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Environments</option>
              <option value="prod">Production</option>
              <option value="dev">Development</option>
              <option value="staging">Staging</option>
              <option value="test">Test</option>
            </select>
          </div>

          {/* Source / Channel */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <select
              value={filters.channel || ''}
              onChange={(e) => handleFilterChange("channel", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Sources</option>
              <option value="webchat,directline">Web</option>
              <option value="msteams">Teams</option>
              <option value="claude-ai">Claude.ai</option>
              <option value="claude-cowork">Claude cowork</option>
              <option value="claude-code">Claude Code</option>
              <option value="claude-ai,claude-cowork,claude-code">All Claude (MCP)</option>
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Error Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Errors
            </label>
            <select
              value={filters.hasErrors === undefined ? "" : filters.hasErrors ? "true" : "false"}
              onChange={(e) => handleFilterChange("hasErrors", e.target.value === "" ? undefined : e.target.value === "true")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Conversations</option>
              <option value="true">With Errors ⚠️</option>
              <option value="false">No Errors</option>
            </select>
          </div>

          {/* Positive Feedback Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Positive Feedback
            </label>
            <select
              value={filters.hasPositiveFeedback === undefined ? "" : filters.hasPositiveFeedback ? "true" : "false"}
              onChange={(e) => handleFilterChange("hasPositiveFeedback", e.target.value === "" ? undefined : e.target.value === "true")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Conversations</option>
              <option value="true">With Thumbs Up 👍</option>
              <option value="false">No Thumbs Up</option>
            </select>
          </div>

          {/* Negative Feedback Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Negative Feedback
            </label>
            <select
              value={filters.hasNegativeFeedback === undefined ? "" : filters.hasNegativeFeedback ? "true" : "false"}
              onChange={(e) => handleFilterChange("hasNegativeFeedback", e.target.value === "" ? undefined : e.target.value === "true")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Conversations</option>
              <option value="true">With Thumbs Down 👎</option>
              <option value="false">No Thumbs Down</option>
            </select>
          </div>

          {/* Rate Limits Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rate Limits
            </label>
            <select
              value={filters.hitLimits === undefined ? "" : filters.hitLimits ? "true" : "false"}
              onChange={(e) => handleFilterChange("hitLimits", e.target.value === "" ? undefined : e.target.value === "true")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Conversations</option>
              <option value="true">Hit Rate Limits 🚫</option>
              <option value="false">No Rate Limits</option>
            </select>
          </div>

          {/* Min Time Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Time (ms)
            </label>
            <input
              type="number"
              value={filters.minTimeMs || ''}
              onChange={(e) => handleFilterChange("minTimeMs", e.target.value)}
              placeholder="e.g. 1000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Max Time Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Time (ms)
            </label>
            <input
              type="number"
              value={filters.maxTimeMs || ''}
              onChange={(e) => handleFilterChange("maxTimeMs", e.target.value)}
              placeholder="e.g. 30000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Conversations {pagination && `(${pagination.total_items})`}
        </h2>
        <div className="flex items-center space-x-4">
          {/* Page Size Selector */}
          {pagination && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Show:</label>
              <select
                value={filters.page_size}
                onChange={(e) => handleFilterChange("page_size", parseInt(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">per page</span>
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-1 rounded-md">
              API Error: {error}
            </div>
          )}
        </div>
      </div>

      {/* Conversations Table */}
      <p className="text-xs text-gray-500 mb-1">All times shown in {getTimezoneAbbreviation()}</p>
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conversation ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entra OID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Updated At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Turns
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {conversations.map((conversation) => (
                <tr
                  key={conversation.conversation_id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => {
                    const url = `/admin/conversation/${conversation.conversation_id}`;
                    if (e.ctrlKey || e.metaKey) {
                      window.open(url, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(url);
                    }
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                    {conversation.conversation_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {conversation.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">
                    {conversation.entra_oid ? (
                      <span title={conversation.entra_oid}>
                        {conversation.entra_oid.substring(0, 8)}...
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      conversation.environment === 'prod' ? 'bg-red-100 text-red-800' :
                      conversation.environment === 'dev' ? 'bg-blue-100 text-blue-800' :
                      conversation.environment === 'staging' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {conversation.environment}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const meta = channelMeta(conversation.channel_id);
                      return (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${meta.cls}`}>
                          {meta.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(conversation.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(conversation.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {conversation.turn_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center space-x-2">
                      <span>{formatDuration(conversation.total_time_ms)}</span>
                      {conversation.hit_limits && (
                        <span className="text-orange-600" title="Rate limits were hit during this conversation">
                          🚫
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-col">
                      <span className="font-medium">
                        {formatCost(getTotalCostFromApi(
                          conversation.total_input_cost_usd,
                          conversation.total_output_cost_usd
                        ))}
                      </span>
                      {(conversation.total_prompt_tokens || conversation.total_completion_tokens) && (
                        <span className="text-xs text-gray-500">
                          {((conversation.total_prompt_tokens || 0) + (conversation.total_completion_tokens || 0)).toLocaleString()} tokens
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      {conversation.has_errors && (
                        <span className="text-red-600" title={`${conversation.error_turns_count} turns with errors`}>
                          ⚠️
                        </span>
                      )}
                      {conversation.has_sharepoint_denials && (
                        <span className="text-purple-600" title="SharePoint access denied for some documents">
                          🔒
                        </span>
                      )}
                      {conversation.has_positive_feedback && (
                        <span className="text-green-600" title="Has positive feedback">
                          👍
                        </span>
                      )}
                      {conversation.has_negative_feedback && (
                        <span className="text-red-600" title="Has negative feedback">
                          👎
                        </span>
                      )}
                      {!conversation.has_errors && !conversation.has_positive_feedback && !conversation.has_negative_feedback && !conversation.has_sharepoint_denials && (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <a
                      href={`/admin/conversation/${conversation.conversation_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View Details
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {conversations.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-gray-500">No conversations found matching the current filters.</div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">{((pagination.page - 1) * pagination.page_size) + 1}</span>
                {' '}to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.page_size, pagination.total_items)}
                </span>
                {' '}of{' '}
                <span className="font-medium">{pagination.total_items}</span>
                {' '}results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.has_prev}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                {(() => {
                  const current = pagination.page;
                  const total = pagination.total_pages;
                  const pages = [];

                  // Always show first page
                  if (total > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => handlePageChange(1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          1 === current
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        1
                      </button>
                    );
                  }

                  // Add ellipsis if needed
                  if (current > 4) {
                    pages.push(
                      <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    );
                  }

                  // Add pages around current page
                  const start = Math.max(2, current - 1);
                  const end = Math.min(total - 1, current + 1);

                  for (let page = start; page <= end; page++) {
                    if (page !== 1 && page !== total) {
                      pages.push(
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === current
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                  }

                  // Add ellipsis if needed
                  if (current < total - 3) {
                    pages.push(
                      <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        ...
                      </span>
                    );
                  }

                  // Always show last page (if more than 1 page)
                  if (total > 1) {
                    pages.push(
                      <button
                        key={total}
                        onClick={() => handlePageChange(total)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          total === current
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {total}
                      </button>
                    );
                  }

                  return pages;
                })()}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.has_next}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}